/**
 * Device Registration Utility - Production Ready
 * Identifies devices by hardware/OS (not browser)
 * Supports remote logout and real-time session management
 */

import { supabase } from '../supabaseClient';

// Generate a unique device fingerprint based on hardware/OS characteristics
const generateDeviceFingerprint = () => {
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    const screenRes = `${window.screen.width}x${window.screen.height}`;
    const colorDepth = window.screen.colorDepth;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Create a fingerprint string from hardware characteristics (not browser-specific)
    const fingerprintData = `${platform}|${screenRes}|${colorDepth}|${timezone}`;

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprintData.length; i++) {
        const char = fingerprintData.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
};

// Detect device type and name based on platform (not browser)
const detectDevice = () => {
    const ua = navigator.userAgent;
    const platform = navigator.platform;

    let deviceType = 'desktop';
    let deviceName = 'Unknown Device';
    let os = 'Unknown OS';
    let osVersion = '';

    // Detect OS and device type
    if (/iPhone/i.test(ua)) {
        deviceType = 'mobile';
        deviceName = 'iPhone';
        os = 'iOS';
        const match = ua.match(/iPhone OS (\d+[_\.]\d+)/);
        if (match) osVersion = match[1].replace('_', '.');
    } else if (/iPad/i.test(ua)) {
        deviceType = 'tablet';
        deviceName = 'iPad';
        os = 'iPadOS';
        const match = ua.match(/CPU OS (\d+[_\.]\d+)/);
        if (match) osVersion = match[1].replace('_', '.');
    } else if (/Android/i.test(ua)) {
        deviceType = /Mobile/i.test(ua) ? 'mobile' : 'tablet';
        deviceName = 'Android Device';
        os = 'Android';
        const match = ua.match(/Android (\d+\.?\d*)/);
        if (match) osVersion = match[1];
        // Try to get device model
        const modelMatch = ua.match(/;\s*([^;)]+)\s*Build/);
        if (modelMatch) deviceName = modelMatch[1].trim();
    } else if (/Macintosh/i.test(ua) || platform === 'MacIntel') {
        deviceType = 'laptop';
        deviceName = 'Mac';
        os = 'macOS';
        const match = ua.match(/Mac OS X (\d+[_\.]\d+[_\.]?\d*)/);
        if (match) osVersion = match[1].replace(/_/g, '.');
        // Detect if it's likely a MacBook vs iMac vs Mac mini (best guess)
        if (window.screen.width <= 1680) deviceName = 'MacBook';
        else if (window.screen.width >= 2560) deviceName = 'iMac';
    } else if (/Windows/i.test(ua)) {
        deviceType = 'desktop';
        deviceName = 'Windows PC';
        os = 'Windows';
        if (/Windows NT 10/i.test(ua)) osVersion = '10/11';
        else if (/Windows NT 6.3/i.test(ua)) osVersion = '8.1';
        else if (/Windows NT 6.2/i.test(ua)) osVersion = '8';
        else if (/Windows NT 6.1/i.test(ua)) osVersion = '7';
    } else if (/Linux/i.test(ua)) {
        deviceType = 'desktop';
        deviceName = 'Linux PC';
        os = 'Linux';
    } else if (/CrOS/i.test(ua)) {
        deviceType = 'laptop';
        deviceName = 'Chromebook';
        os = 'Chrome OS';
    }

    return { deviceType, deviceName, os, osVersion };
};

// Get IP-based location (using free, CORS-enabled API)
const getLocationFromIP = async () => {
    try {
        const response = await fetch('https://get.geojs.io/v1/ip/geo.json', {
            signal: AbortSignal.timeout(5000)
        });
        if (response.ok) {
            const data = await response.json();
            return {
                ip: data.ip,
                city: data.city,
                region: data.region,
                country: data.country,
                latitude: data.latitude,
                longitude: data.longitude
            };
        }
    } catch (error) {
        // Silent catch to keep console clean
    }

    return null;
};

// Store current device ID in localStorage for session management
const DEVICE_ID_KEY = 'fozzil_device_id';
const SESSION_TOKEN_KEY = 'fozzil_session_token';

export const getCurrentDeviceId = () => {
    return localStorage.getItem(DEVICE_ID_KEY);
};

export const setCurrentDeviceId = (id) => {
    localStorage.setItem(DEVICE_ID_KEY, id);
};

export const getSessionToken = () => {
    return localStorage.getItem(SESSION_TOKEN_KEY);
};

export const setSessionToken = (token) => {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
};

// Generate a secure session token
const generateSessionToken = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Register or update device (production-ready)
export const registerDevice = async (userId) => {
    if (!userId) return null;

    // Clear any stale session data from previous logins
    // This prevents loops when old/invalid tokens are stored
    const oldDeviceId = getCurrentDeviceId();
    if (oldDeviceId) {
        // Check if old device still belongs to this user
        const { data: existingDevice, error } = await supabase
            .from('user_devices')
            .select('user_id')
            .eq('id', oldDeviceId)
            .single();

        // If device doesn't exist or belongs to different user, clear stale data
        if (error || !existingDevice || existingDevice.user_id !== userId) {
            console.log('Clearing stale device session data');
            localStorage.removeItem(DEVICE_ID_KEY);
            localStorage.removeItem(SESSION_TOKEN_KEY);
        }
    }

    const fingerprint = generateDeviceFingerprint();
    const { deviceType, deviceName, os, osVersion } = detectDevice();
    const sessionToken = generateSessionToken();

    // Get location (non-blocking - don't fail if it doesn't work)
    let location = null;
    try {
        location = await getLocationFromIP();
    } catch (e) {
        console.warn('Could not get location');
    }

    // Check if this device already exists (same fingerprint for this user)
    // Also silently clean up any devices inactive for 3+ months
    cleanupStaleDevices(userId); // Fire-and-forget, don't await

    const { data: existingDevices, error: fetchError } = await supabase
        .from('user_devices')
        .select('*')
        .eq('user_id', userId)
        .eq('device_fingerprint', fingerprint);

    if (fetchError) {
        console.error('Error fetching devices:', fetchError);
        return null;
    }

    let deviceId;
    let isNewDevice = false;

    if (existingDevices && existingDevices.length > 0) {
        // Update existing device
        const existingDevice = existingDevices[0];
        deviceId = existingDevice.id;

        const { error } = await supabase
            .from('user_devices')
            .update({
                last_active: new Date().toISOString(),
                is_current: true,
                session_token: sessionToken,
                session_valid: true,
                ip_address: location?.ip || existingDevice.ip_address,
                location_city: location?.city || existingDevice.location_city,
                location_region: location?.region || existingDevice.location_region,
                location_country: location?.country || existingDevice.location_country,
                latitude: location?.latitude || existingDevice.latitude,
                longitude: location?.longitude || existingDevice.longitude
            })
            .eq('id', existingDevice.id);

        if (error) {
            console.error('Error updating device:', error);
            return null;
        }
    } else {
        // Insert new device — NOT trusted by default
        isNewDevice = true;
        const { data, error } = await supabase
            .from('user_devices')
            .insert([{
                user_id: userId,
                device_fingerprint: fingerprint,
                device_name: deviceName,
                device_type: deviceType,
                os: os,
                os_version: osVersion,
                is_current: true,
                is_trusted: false,
                session_token: sessionToken,
                session_valid: true,
                ip_address: location?.ip,
                location_city: location?.city,
                location_region: location?.region,
                location_country: location?.country,
                latitude: location?.latitude,
                longitude: location?.longitude
            }])
            .select()
            .single();

        if (error) {
            console.error('Error registering device:', error);
            return null;
        }

        deviceId = data.id;
    }

    // Store device ID and session token locally
    setCurrentDeviceId(deviceId);
    setSessionToken(sessionToken);

    return { deviceId, isNewDevice };
};

// Update last active timestamp (call periodically or on activity)
export const updateDeviceActivity = async () => {
    const deviceId = getCurrentDeviceId();
    if (!deviceId) return;

    const { error } = await supabase
        .from('user_devices')
        .update({ last_active: new Date().toISOString() })
        .eq('id', deviceId);

    if (error) console.error('Error updating activity:', error);
};

// Check if current session is still valid (for real-time logout detection)
export const checkSessionValidity = async () => {
    const deviceId = getCurrentDeviceId();
    const sessionToken = getSessionToken();

    // If no device registered yet, return true (don't trigger logout)
    // This prevents logout loops when user is signing in
    if (!deviceId || !sessionToken) return true;

    const { data, error } = await supabase
        .from('user_devices')
        .select('session_valid, session_token')
        .eq('id', deviceId)
        .single();

    // If error fetching (device might not exist yet), return true
    if (error || !data) return true;

    // Check if session is still valid AND token matches
    return data.session_valid && data.session_token === sessionToken;
};

// Remote logout - invalidate session on a specific device
export const remoteLogoutDevice = async (deviceId) => {
    const { error } = await supabase
        .from('user_devices')
        .update({
            session_valid: false,
            is_current: false
        })
        .eq('id', deviceId);

    if (error) {
        console.error('Error invalidating session:', error);
        return false;
    }
    return true;
};

// Remove device from account completely
export const removeDevice = async (deviceId) => {
    // First invalidate session, then delete
    await remoteLogoutDevice(deviceId);

    const { error } = await supabase
        .from('user_devices')
        .delete()
        .eq('id', deviceId);

    if (error) {
        console.error('Error removing device:', error);
        return false;
    }
    return true;
};

// Auto-cleanup: Remove devices inactive for 3+ months
// Runs silently in the background — no frontend indication
const cleanupStaleDevices = async (userId) => {
    if (!userId) return;

    try {
        const THREE_MONTHS_AGO = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();

        // Delete devices whose last_active is older than 3 months
        const { error } = await supabase
            .from('user_devices')
            .delete()
            .eq('user_id', userId)
            .lt('last_active', THREE_MONTHS_AGO);

        if (error) {
            // Silent failure — don't block the user flow
            console.warn('Stale device cleanup failed:', error.message);
        }
    } catch (e) {
        // Completely silent
    }
};

// Get all user devices
export const getUserDevices = async (userId) => {
    if (!userId) return [];

    // Silently clean up devices inactive for 3+ months before fetching
    await cleanupStaleDevices(userId);

    const { data, error } = await supabase
        .from('user_devices')
        .select('*')
        .eq('user_id', userId)
        .order('last_active', { ascending: false });

    if (error) {
        console.error('Error fetching devices:', error);
        return [];
    }

    return data || [];
};

// Trust a device
export const trustDevice = async (deviceId) => {
    const { error } = await supabase
        .from('user_devices')
        .update({ is_trusted: true })
        .eq('id', deviceId);

    if (error) {
        console.error('Error trusting device:', error);
        return false;
    }
    return true;
};

// Untrust a device
export const untrustDevice = async (deviceId) => {
    const { error } = await supabase
        .from('user_devices')
        .update({ is_trusted: false })
        .eq('id', deviceId);

    if (error) {
        console.error('Error untrusting device:', error);
        return false;
    }
    return true;
};

// Check if device is online (active within last 5 minutes)
export const isDeviceOnline = (lastActive) => {
    if (!lastActive) return false;
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return new Date(lastActive) > fiveMinutesAgo;
};

// Format last active time
export const formatLastActive = (lastActive) => {
    if (!lastActive) return 'Never';

    const now = new Date();
    const active = new Date(lastActive);
    const diffMs = now - active;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return active.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Get device icon name based on device type
export const getDeviceIconType = (deviceType, deviceName) => {
    if (deviceName?.includes('iPhone')) return 'iphone';
    if (deviceName?.includes('iPad')) return 'ipad';
    if (deviceName?.includes('MacBook') || deviceName?.includes('Mac')) return 'mac';
    if (deviceName?.includes('iMac')) return 'imac';
    if (deviceType === 'mobile') return 'mobile';
    if (deviceType === 'tablet') return 'tablet';
    return 'desktop';
};

// Subscribe to real-time session changes (for detecting remote logout)
export const subscribeToSessionChanges = (deviceId, onSessionInvalidated) => {
    if (!deviceId) return null;

    const subscription = supabase
        .channel(`device-${deviceId}`)
        .on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'user_devices',
                filter: `id=eq.${deviceId}`
            },
            (payload) => {
                if (payload.new && payload.new.session_valid === false) {
                    onSessionInvalidated();
                }
            }
        )
        .subscribe();

    return subscription;
};

// Cleanup on logout
export const cleanupDeviceSession = async () => {
    const deviceId = getCurrentDeviceId();
    if (deviceId) {
        await supabase
            .from('user_devices')
            .update({ is_current: false, session_valid: false })
            .eq('id', deviceId);
    }

    localStorage.removeItem(DEVICE_ID_KEY);
    localStorage.removeItem(SESSION_TOKEN_KEY);
};
